@let userData = model.state.user();
@let newUserMode = !userData.id;
@let authMethodUsername = model.state.authMethodUsername();

<form (submit)="onFormSubmit()" [formGroup]="model.userForm" class="scrollbar-gray">
  @if (model.state.forceRole() === TYPES.UserRole.FleetOwner) {
    <p class="text-14 fw-500 color-gray-500 mb-16">
      {{ 'users.form.force-fleet-owner-role' | translate }}
    </p>
  }

  <p-fluid class="inputs-section">
    <div class="label mb-8 text-14 fw-500">
      {{ 'users.form.name' | translate }}
      @if (newUserMode) {
        <span class="required">*</span>
      }
    </div>
    <input
      class="mb-16 h--40"
      data-test-id="users.form.name"
      formControlName="name"
      pInputText
      placeholder="{{ 'users.form.name-placeholder' | translate }}"
      type="text"
    />

    <div class="label mb-8 text-14 fw-500">
      {{ 'users.form.email' | translate }}
      @if (newUserMode && !authMethodUsername) {
        <span class="required">*</span>
      }
    </div>
    <input
      [appForceInvalidClasses]="!!model.state.emailError()"
      class="h--40 mb-16"
      data-test-id="users.form.email"
      formControlName="email"
      pInputText
      placeholder="email@telematics.com"
      type="text"
    />

    @if (authMethodUsername) {
      <app-user-credentials-form
        (closed)="onDisableUsernameAuth()"
        [form]="model.credentialsForm"
        [isEditMode]="!newUserMode"
        [usernameError]="model.state.usernameError()"
        [usernamePrefix]="usernamePrefix"
        class="flex mb-8"
      ></app-user-credentials-form>
    } @else if (newUserMode) {
      <span class="flex--col mb-8">
        <span class="text-14 fw-500 color-gray-500">
          {{ 'users.form.invitation-hint' | translate }}

          <span (click)="onEnableUsernameAuth()" class="color-primary-500 underline clickable">
            {{ 'users.form.invitation-link' | translate }}
          </span>
        </span>
      </span>
    }

    @if (!newUserMode) {
      <div class="flex justify-space-between mb-16 mt-16">
        <div class="label text-14 fw-500">
          {{ 'users.form.2fa' | translate }}
        </div>
        <div class="text-13 fw-400 color-gray-500">
          {{
            (userData.mfaStatus?.includes(TYPES.MfaMethod.TOTP)
              ? 'common.enabled'
              : 'common.disabled'
            ) | translate
          }}
        </div>
      </div>
    }

    <div class="label mb-8 text-14 fw-500">
      {{ 'users.form.phone' | translate }}
    </div>

    <div class="field mb-16">
      <app-phone-number-input
        [defaultCountryCode]="model.state.country()"
        data-test-id="users.form.phone"
        formControlName="phoneNumber"
        [mobilePanelTitle]="'users.form.phone' | translate"
      ></app-phone-number-input>

      @if (
        model.userForm.controls.phoneNumber.invalid && model.userForm.controls.phoneNumber.touched
      ) {
        <div class="errors-box">
          @if (model.userForm.controls.phoneNumber.hasError('phoneNumber')) {
            <p class="error-message">
              {{ 'common.validation-errors.phone-number' | translate }}
            </p>
          }
        </div>
      }
    </div>

    @if (groupOptionProvider && mode === 'Fleet' && model.state.groupsAndLabelsAllowed()) {
      <div class="label mb-8 text-14 fw-500">
        {{ 'users.form.group-access' | translate }}
      </div>
      <div class="field mb-4 small">
        <app-multiselect
          formControlName="groupsAccess"
          [options]="(groupOptions$ | async) || []"
          [placeholder]="'users.form.groups-access-placeholder' | translate"
          [mobilePanelTitle]="'users.form.groups-access-placeholder' | translate"
          [maxSelectedLabels]="3"
          [display]="'chip'"
          [optionLabel]="'label'"
          [optionValue]="'value'"
        >
          <!--
          <ng-template pTemplate="header">
            <div class="custom-close-button">
              <app-icon-close
                [color]="'var(&#45;&#45;color-gray-500)'"
                [height]="24"
                [width]="24"
              ></app-icon-close>
            </div>
          </ng-template>
          -->
        </app-multiselect>

        @if (
          model.userForm.controls.groupsAccess.invalid &&
          model.userForm.controls.groupsAccess.touched
        ) {
          <div class="errors-box">
            @if (model.userForm.controls.groupsAccess.hasError('required')) {
              <p class="error-message">
                {{ 'users.form.errors.groups-required' | translate }}
              </p>
            }
          </div>
        }
      </div>

      @if (!model.state.emptyGroupsListWarningDisplayed()) {
        <p class="description text-12 fw-400 mb-16">
          {{ 'users.form.groups-access-description' | translate }}
        </p>
      } @else {
        <div class="warning-message mb-16">
          <app-icon-warning
            [width]="20"
            [height]="20"
            [color]="'var(--color-secondary-500)'"
          ></app-icon-warning>
          <div class="text text-14 fw-400">
            {{ 'users.form.groups-access-warning' | translate }}
          </div>
        </div>
      }
    }

    <div class="label mb-8 text-14 fw-500">
      {{ 'users.form.role' | translate }}
      @if (newUserMode) {
        <span class="required">*</span>
      }
    </div>

    <app-select
      class="w100"
      data-test-id="users.form.role"
      formControlName="roleId"
      [options]="roleOptions()"
      [placeholder]="'users.form.select-role' | translate"
      [mobilePanelTitle]="'users.form.role' | translate"
      [mobileSelectionMode]="'instant'"
    >
      <ng-template let-param #selectedItem>
        <ng-container [ngTemplateOutlet]="item" [ngTemplateOutletContext]="{ $implicit: param }">
        </ng-container>
      </ng-template>

      <ng-template let-param #item>
        <div class="option-item flex align-items-center gap-8">
          <div class="text-14 fw-400">
            {{ param.label }}
          </div>
          @if (param.isCustom) {
            <p-tag class="custom p-4-8" [value]="'roles.grid.custom' | translate"> </p-tag>
          }
        </div>
      </ng-template>
    </app-select>
  </p-fluid>

  @if (model.state.emailError(); as error) {
    <div class="errors-section mt-16">
      <div class="errors-section-icon">
        <app-icon-x-circle
          [color]="'var(--color-alert-500)'"
          [height]="20"
          [width]="20"
        ></app-icon-x-circle>
      </div>
      <div class="errors-section-text text-12 fw-400">{{ error }}</div>
    </div>
  }
</form>

<div class="footer flex gap-12">
  <p-button
    (click)="cancel()"
    [severity]="'secondary'"
    class="cancel w-grow text-14 fw-600 h--40"
    data-test-id="users.form.cancel-btn"
  >
    {{ 'button.cancel' | translate }}
  </p-button>

  <p-button
    (click)="onFormSubmit()"
    [disabled]="!model.isValid || model.state.isLoading()"
    [loading]="model.state.isLoading()"
    class="submit text-14 fw-600 min-w50 h--40"
    data-test-id="users.form.submit-btn"
    type="submit"
  >
    @if (newUserMode) {
      @if (authMethodUsername) {
        {{ 'users.form.add-user' | translate }}
      } @else {
        <app-icon-send [height]="20" [width]="20" class="icon"></app-icon-send>
        {{ 'users.form.invite-user' | translate }}
      }
    } @else {
      <app-icon-check [height]="20" [width]="20" class="icon"></app-icon-check>
      {{ 'button.save' | translate }}
    }
  </p-button>
</div>
